import { $convertToMarkdownString } from '@lexical/markdown';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $isTableCellNode,
  $isTableNode,
  $isTableRowNode,
  getTableElement,
  getTableObserverFromTableElement,
} from '@lexical/table';
import {
  $getNodeByKey,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  $setSelection,
  COMMAND_PRIORITY_NORMAL,
  createCommand,
  LexicalCommand,
} from 'lexical';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useRecordEditAction } from 'src/UI/app-container/sidebar/useRecordEditAction';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { useInlineDocumentEdit } from 'src/document-editor/hooks/useInlineDocumentEdit';
import { createTransformers } from '../config/transformers';
import { $createSuggestionNode } from '../nodes/SuggestionNode';
import { TableSelectionInfo } from '../types';
import {
  $getTableCellMarkdown,
  $getTableSelectionMarkdown,
  $insertReplacementMarkers,
  $isPartialColumnSelection,
  $removeReplacementMarkers,
  $setEditorValue,
  hoistReplacementTags,
} from '../utils';

type ScrollLock = Array<{ el: HTMLElement; top: number; overflow: string }>;

function lockScroll(element: HTMLElement | null): ScrollLock {
  const locks: ScrollLock = [];
  let current = element?.parentElement;
  while (current && current !== document.documentElement) {
    const style = getComputedStyle(current);
    const overflowY = style.overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      current.scrollHeight > current.clientHeight
    ) {
      locks.push({ el: current, top: current.scrollTop, overflow: current.style.overflow });
      current.style.overflow = 'hidden';
    }
    current = current.parentElement;
  }
  return locks;
}

function unlockScroll(locks: ScrollLock): void {
  for (const { el, top, overflow } of locks) {
    el.style.overflow = overflow;
    el.scrollTop = top;
  }
}

type PendingRequest = {
  markdown: string;
  promptText: string;
  tableSelectionInfo?: TableSelectionInfo;
};

type SiestaRequestPayload = {
  promptText: string;
  toolbarTopPercent?: number;
  tableSelectionInfo?: TableSelectionInfo;
};

function $findAncestorTableCellInfo(
  selection: ReturnType<typeof $getSelection>,
): TableSelectionInfo | null {
  if (!$isRangeSelection(selection)) return null;

  const anchorCell = selection.anchor.getNode().getParents().find($isTableCellNode);
  if (!anchorCell || !$isTableCellNode(anchorCell)) return null;

  const row = anchorCell.getParent();
  if (!row || !$isTableRowNode(row)) return null;

  const table = row.getParent();
  if (!table || !$isTableNode(table)) return null;

  const rows = table.getChildren().filter($isTableRowNode);
  const rowIdx = rows.indexOf(row);
  const colIdx = row.getChildren().filter($isTableCellNode).indexOf(anchorCell);

  if (rowIdx === -1 || colIdx === -1) return null;

  return {
    tableNodeKey: table.getKey(),
    startRow: rowIdx,
    endRow: rowIdx,
    startCol: colIdx,
    endCol: colIdx,
  };
}

export const SIESTA_SELECTION_REQUEST_COMMAND: LexicalCommand<SiestaRequestPayload> = createCommand(
  'SIESTA_SELECTION_REQUEST',
);

type Props = {
  documentId?: string;
  clientId?: string;
  conversationId?: string;
  onProcessingComplete?: () => void;
  onInlineEditStart?: (top: number) => void;
  onInlineEditComplete?: () => void;
};

export function SiestaInlinePlugin({
  documentId,
  clientId,
  conversationId,
  onProcessingComplete,
  onInlineEditStart,
  onInlineEditComplete,
}: Props) {
  const [editor] = useLexicalComposerContext();
  const { inlineEdit } = useInlineDocumentEdit(documentId);
  const { recordAction } = useRecordEditAction();
  const transformers = useMemo(() => createTransformers(editor), [editor]);
  const onInlineEditStartRef = useRef(onInlineEditStart);
  const onInlineEditCompleteRef = useRef(onInlineEditComplete);
  useEffect(() => {
    onInlineEditStartRef.current = onInlineEditStart;
    onInlineEditCompleteRef.current = onInlineEditComplete;
  }, [onInlineEditStart, onInlineEditComplete]);

  const [pendingRequest, setPendingRequest] = useState<PendingRequest | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  function finalizeRequest() {
    setPendingRequest(null);
    onProcessingComplete?.();
    onInlineEditCompleteRef.current?.();
  }

  useEffect(() => {
    if (!pendingRequest || !documentId || !clientId) return;

    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    const { markdown, promptText, tableSelectionInfo } = pendingRequest;

    inlineEdit({
      documentContent: markdown,
      editRequest: promptText,
      clientId,
      signal: controller.signal,
    })
      .then(replacementMarkdown => {
        if (controller.signal.aborted) return;

        const hasDeletion = replacementMarkdown.includes('source="deletion"');
        const hasInline = replacementMarkdown.includes('source="inline"');
        const isPureDeletion = hasDeletion && !hasInline;

        let isPartial = false;
        if (tableSelectionInfo && isPureDeletion) {
          editor.getEditorState().read(() => {
            isPartial = $isPartialColumnSelection(tableSelectionInfo);
          });
        }

        if (tableSelectionInfo && isPureDeletion && isPartial) {
          editor.update(
            () => {
              const tableNode = $getNodeByKey(tableSelectionInfo.tableNodeKey);
              if (!tableNode || !$isTableNode(tableNode)) return;

              const rows = tableNode.getChildren().filter($isTableRowNode);
              for (
                let rowIdx = tableSelectionInfo.startRow;
                rowIdx <= tableSelectionInfo.endRow;
                rowIdx++
              ) {
                const row = rows[rowIdx];
                if (!row) continue;
                const cells = row.getChildren().filter($isTableCellNode);
                for (
                  let colIdx = tableSelectionInfo.startCol;
                  colIdx <= tableSelectionInfo.endCol;
                  colIdx++
                ) {
                  const cell = cells[colIdx];
                  if (!cell) continue;
                  const suggestion = $createSuggestionNode('deletion', true);
                  const children = cell.getChildren();
                  suggestion.append(...children);
                  cell.append(suggestion);
                }
              }
            },
            { tag: 'siesta-replacement' },
          );

          finalizeRequest();
          return;
        }

        const locks = lockScroll(editor.getRootElement());

        editor.update(
          () => {
            $setEditorValue(replacementMarkdown, transformers);
          },
          { tag: 'siesta-replacement' },
        );

        requestAnimationFrame(() => {
          unlockScroll(locks);
        });

        finalizeRequest();
      })
      .catch(error => {
        if (controller.signal.aborted) return;
        notifyAndLogError('Siesta AI Error', 'Failed to transform text. Please try again.', error, {
          prompt: promptText,
          markdown,
        });
        finalizeRequest();
      });
  }, [
    pendingRequest,
    editor,
    transformers,
    documentId,
    clientId,
    inlineEdit,
    onProcessingComplete,
  ]);

  useEffect(() => {
    return editor.registerCommand(
      SIESTA_SELECTION_REQUEST_COMMAND,
      (payload: SiestaRequestPayload) => {
        const { promptText, toolbarTopPercent, tableSelectionInfo } = payload;

        if (documentId && conversationId) {
          recordAction({
            documentId,
            actionText: promptText,
            source: 'MANUAL',
            conversationId,
            clientId,
          });
        }

        const locks = lockScroll(editor.getRootElement());

        editor.update(() => {
          function dispatchTableSelection(info: TableSelectionInfo) {
            const isSingleCell = info.startRow === info.endRow && info.startCol === info.endCol;
            const tableNode = $getNodeByKey(info.tableNodeKey);
            const totalCols = tableNode && $isTableNode(tableNode) ? tableNode.getColumnCount() : 1;
            const spansAllColumns = info.startCol === 0 && info.endCol >= totalCols - 1;
            const useCellScope = isSingleCell && !spansAllColumns;

            const markdown = useCellScope
              ? $getTableCellMarkdown(info.tableNodeKey, info.startRow, info.startCol, transformers)
              : $getTableSelectionMarkdown(
                  info.tableNodeKey,
                  info.startRow,
                  info.endRow,
                  transformers,
                );

            if (!markdown?.trim()) {
              finalizeRequest();
              return;
            }

            $setSelection(null);
            onInlineEditStartRef.current?.(toolbarTopPercent ?? 50);
            setPendingRequest({ markdown, promptText, tableSelectionInfo: info });
          }

          if (tableSelectionInfo) {
            const tableNode = $getNodeByKey(tableSelectionInfo.tableNodeKey);
            if (tableNode && $isTableNode(tableNode)) {
              const tableElement = getTableElement(
                tableNode,
                editor.getElementByKey(tableNode.getKey()),
              );
              if (tableElement) {
                const tableObserver = getTableObserverFromTableElement(tableElement);
                tableObserver?.$clearHighlight();
              }
            }

            dispatchTableSelection(tableSelectionInfo);
            return;
          }

          const selection = $getSelection();

          if (!$isRangeSelection(selection) || selection.isCollapsed()) {
            return;
          }

          // Range selections inside table cells are redirected to the table
          // selection path to avoid broken replacement markers.
          const derivedTableInfo = $findAncestorTableCellInfo(selection);
          if (derivedTableInfo) {
            dispatchTableSelection(derivedTableInfo);
            return;
          }

          const markers = $insertReplacementMarkers();
          if (!markers) return;

          const root = $getRoot();
          const markdown = hoistReplacementTags($convertToMarkdownString(transformers, root));

          $removeReplacementMarkers(markers);

          if (!markdown.trim()) {
            return;
          }

          $setSelection(null);

          onInlineEditStartRef.current?.(toolbarTopPercent ?? 50);
          setPendingRequest({ markdown, promptText });
        });

        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            unlockScroll(locks);
          });
        });

        return true;
      },
      COMMAND_PRIORITY_NORMAL,
    );
  }, [editor, transformers, documentId, conversationId, clientId, recordAction]);

  return null;
}
